/**
 * Publish guard helpers for event publishing.
 * 
 * P0 INVARIANT: "Aktiviteter får inte bli publika om platsen är draft"
 * - Producer AND place must be public for activity to be publishable
 * - UI guards (disabled button) + server guards (RPC reject) are both required
 * 
 * v1 decision: pending + active producers CAN publish events IF place is public.
 * Only moderation blocks (paused/banned/suspended).
 * Producer must also be publicly visible (status='public').
 */

import { isPlacePublic } from '@/lib/status';

interface ProducerLike {
  status?: string | null;
  producer_status?: string | null;
  suspended_at?: string | null;
  place_id?: string | null;
}

interface PlaceLike {
  id?: string;
  status?: string | null;
}

interface PublishBlockReason {
  title: string;
  text: string;
  cta?: { label: string; route: string };
}

/** Rich publish-guard result used by event editor UI */
export interface PublishGuardResult {
  ok: boolean;
  reason?: string;
  cta?: { label: string; to: string };
}

/**
 * Returns a rich guard result for the Publicera button.
 * Includes reason text + optional CTA link.
 * 
 * Checks: producer.status + place.status + producer moderation status
 */
export function checkPublishGuard(
  producer: ProducerLike | null | undefined,
  farmId?: string,
  place?: PlaceLike | null
): PublishGuardResult {
  if (!producer) {
    return { ok: false, reason: 'Vi kunde inte läsa in din gård. Försök igen.' };
  }
  if (producer.suspended_at || producer.producer_status === 'paused' || producer.producer_status === 'banned') {
    return { ok: false, reason: 'Din verksamhet är pausad eller avstängd. Kontakta support.' };
  }
  
  // P0 INVARIANT: Place must be public
  if (!place || !isPlacePublic(place.status)) {
    return {
      ok: false,
      reason: 'För att publicera en aktivitet måste din sida vara synlig för besökare. Gör sidan synlig först.',
      cta: farmId
        ? { label: 'Gå till Sida', to: `/app/producer/farm/${farmId}/plats` }
        : undefined,
    };
  }
  
  // Producer must be public
  if (producer.status !== 'public') {
    return {
      ok: false,
      reason: 'Du kan spara som utkast. För att publicera behöver du först göra din gård synlig.',
      cta: farmId
        ? { label: 'Gör gården synlig', to: `/app/producer/farm/${farmId}` }
        : undefined,
    };
  }
  return { ok: true };
}

/** Simple boolean for quick checks (backwards compat) */
export function canPublishEvent(producer: ProducerLike | null | undefined, place?: PlaceLike | null): boolean {
  return checkPublishGuard(producer, undefined, place).ok;
}

export function getPublishBlockReason(
  producer: ProducerLike | null | undefined,
  farmId?: string,
  place?: PlaceLike | null
): PublishBlockReason | null {
  if (!producer) {
    return { title: 'Kunde inte läsa in gården', text: 'Vi kunde inte läsa in din gård. Försök igen.' };
  }

  if (producer.suspended_at || producer.producer_status === 'paused' || producer.producer_status === 'banned') {
    return {
      title: 'Publicering spärrad',
      text: 'Din verksamhet är pausad eller avstängd. Kontakta support.',
    };
  }

  // P0 INVARIANT: Place must be public
  if (!place || !isPlacePublic(place.status)) {
    return {
      title: 'Din sida är inte synlig ännu',
      text: 'För att publicera en aktivitet måste din sida vara synlig för besökare. Gör sidan synlig först.',
      cta: farmId
        ? { label: 'Gå till Sida', route: `/app/producer/farm/${farmId}/plats` }
        : undefined,
    };
  }

  if (producer.status !== 'public') {
    return {
      title: 'Gården är inte synlig ännu',
      text: 'Du kan spara som utkast. För att publicera behöver du först göra din gård synlig.',
      cta: farmId
        ? { label: 'Gör gården synlig', route: `/app/producer/farm/${farmId}` }
        : undefined,
    };
  }

  return null;
}

/**
 * Parse Postgres P0001 errors from Supabase into user-friendly messages.
 */
export function parsePublishError(error: unknown): { title: string; text: string } {
  if (error && typeof error === 'object' && 'code' in error) {
    const pgError = error as { code?: string; message?: string };
    if (pgError.code === 'P0001' && pgError.message) {
      return { title: 'Kan inte publicera', text: pgError.message };
    }
  }
  // Also handle error.message directly (supabase wraps it)
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message: string }).message;
    if (msg.includes('Cannot publish event')) {
      return { title: 'Kan inte publicera', text: msg };
    }
  }
  return { title: 'Något gick fel', text: 'Försök igen.' };
}
